import type { EntityState, Reducer } from "@reduxjs/toolkit"

import { loadActiveFarmAsync } from "@/base/load-app"
import { createEntityAdapter, createSlice, createSelector } from "@reduxjs/toolkit"

import { signOutAsync } from "./auth.reducer"
import type { RootState } from "@/redux/root.reducer"

// =============================================================================
// TYPES
// =============================================================================

export type PlanState = "DRAFT" | "ACTIVE" | "PAUSED" | "COMPLETED"

export type IrrigationMode = "NORMAL" | "DRY_PASS" | "SKIP"

export type DepthMode = "SPEED" | "APPLICATION_DEPTH"

export type WaterStateChoice = "ON" | "OFF" | "UNCHANGED"

export type DirectionChoice = "CW" | "CCW" | "UNCHANGED"

export type TriggerDirection = "CW" | "CCW" | "BOTH"

export type BoundaryActionType =
  | "STOP"
  | "REVERSE"
  | "GOTO_ANGLE"
  | "SHUTDOWN"
  | "APPLY_SECTOR"
  | "CUSTOM"

export type PlanExecutionEventType =
  | "PLAN_ACTIVATED"
  | "PLAN_PAUSED"
  | "PLAN_RESUMED"
  | "PLAN_COMPLETED"
  | "SECTOR_ENTERED"
  | "SECTOR_EXITED"
  | "BOUNDARY_CROSSED"
  | "ACTION_EXECUTED"
  | "ACTION_FAILED"
  | "ACTION_SCHEDULED"
  | "COMMAND_SENT"
  | "DRY_PASS_STARTED"
  | "DRY_PASS_ENDED"
  | "ERROR"

/**
 * Defines a geographic sector within an irrigation plan with specific settings.
 */
export interface PlanSector {
  id: number
  irrigationPlanId: number
  name: string | null
  startAngleDegrees: number
  endAngleDegrees: number
  priority: number
  irrigationMode: IrrigationMode
  depthMode: DepthMode
  speedPercent: number | null
  applicationDepthInches: number | null
  waterState: WaterStateChoice
  endGunState: WaterStateChoice
  direction: DirectionChoice
  color: string
  isActive: boolean
  createDate: string
}

/**
 * Defines an action to take when the pivot crosses a specific angle.
 */
export interface BoundaryAction {
  id: number
  irrigationPlanId: number
  name: string | null
  triggerAngleDegrees: number
  triggerDirection: TriggerDirection
  actionType: BoundaryActionType
  targetAngleDegrees: number | null
  delaySeconds: number
  customActionId: number | null
  lastTriggeredAt: string | null
  scheduledActionId: number | null
  triggerCount: number
  isActive: boolean
  createDate: string
}

/**
 * Irrigation plan for a center pivot with variable rate zones.
 */
export interface IrrigationPlan {
  id: number
  name: string
  description: string | null
  centerPivotId: number
  farmId: number
  state: PlanState
  activatedAt: string | null
  activatedByUserId: string | null
  pausedAt: string | null
  completedAt: string | null
  currentSectorId: number | null
  lastPositionCheckAt: string | null
  lastPositionDegrees: number | null
  createdByUserId: string | null
  isActive: boolean
  createDate: string
  sectors: PlanSector[]
  boundaryActions: BoundaryAction[]
}

/**
 * Summary view of irrigation plan for list display.
 */
export interface IrrigationPlanSummary {
  id: number
  name: string
  description: string | null
  centerPivotId: number
  farmId: number
  state: PlanState
  activatedAt: string | null
  sectorCount: number
  boundaryActionCount: number
  isActive: boolean
  createDate: string
}

/**
 * Execution log entry for plan audit trail.
 */
export interface PlanExecutionLog {
  id: number
  irrigationPlanId: number
  eventType: PlanExecutionEventType
  positionDegrees: number | null
  sectorId: number | null
  boundaryActionId: number | null
  scheduledActionId: number | null
  deviceFunctionCallId: number | null
  details: Record<string, unknown> | null
  errorMessage: string | null
  createDate: string
}

// =============================================================================
// ADAPTERS
// =============================================================================

const plansAdapter = createEntityAdapter<IrrigationPlan, number>({
  selectId: (plan) => plan.id,
  sortComparer: (a, b) => {
    // Active plans first, then by creation date (newest first)
    if (a.state === "ACTIVE" && b.state !== "ACTIVE") return -1
    if (b.state === "ACTIVE" && a.state !== "ACTIVE") return 1
    return new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
  },
})

const sectorsAdapter = createEntityAdapter<PlanSector, number>({
  selectId: (sector) => sector.id,
  sortComparer: (a, b) => {
    // Sort by priority (higher first), then by start angle
    if (a.priority !== b.priority) return b.priority - a.priority
    return a.startAngleDegrees - b.startAngleDegrees
  },
})

const boundaryActionsAdapter = createEntityAdapter<BoundaryAction, number>({
  selectId: (action) => action.id,
  sortComparer: (a, b) => a.triggerAngleDegrees - b.triggerAngleDegrees,
})

// =============================================================================
// STATE
// =============================================================================

interface IrrigationPlansState {
  plans: EntityState<IrrigationPlan, number>
  sectors: EntityState<PlanSector, number>
  boundaryActions: EntityState<BoundaryAction, number>
  // UI state
  selectedPlanId: number | null
}

const initialState: IrrigationPlansState = {
  plans: plansAdapter.getInitialState(),
  sectors: sectorsAdapter.getInitialState(),
  boundaryActions: boundaryActionsAdapter.getInitialState(),
  selectedPlanId: null,
}

// =============================================================================
// SLICE
// =============================================================================

const irrigationPlansSlice = createSlice({
  name: "irrigationPlans",
  initialState,
  reducers: {
    // Plan actions
    upsertPlan: (state, action: { payload: IrrigationPlan }) => {
      const plan = action.payload
      plansAdapter.upsertOne(state.plans, plan)

      // Replace sectors for this plan (remove old, add new)
      // This ensures deactivated sectors don't accumulate in Redux
      const existingSectorIds = Object.values(state.sectors.entities)
        .filter((s) => s?.irrigationPlanId === plan.id)
        .map((s) => s!.id)
      sectorsAdapter.removeMany(state.sectors, existingSectorIds)
      if (plan.sectors) {
        sectorsAdapter.upsertMany(state.sectors, plan.sectors)
      }

      // Replace boundary actions for this plan
      const existingActionIds = Object.values(state.boundaryActions.entities)
        .filter((a) => a?.irrigationPlanId === plan.id)
        .map((a) => a!.id)
      boundaryActionsAdapter.removeMany(state.boundaryActions, existingActionIds)
      if (plan.boundaryActions) {
        boundaryActionsAdapter.upsertMany(state.boundaryActions, plan.boundaryActions)
      }
    },
    upsertManyPlans: (state, action: { payload: IrrigationPlan[] }) => {
      const plans = action.payload
      plansAdapter.upsertMany(state.plans, plans)
      // Extract and upsert nested entities
      const allSectors: PlanSector[] = []
      const allActions: BoundaryAction[] = []
      for (const plan of plans) {
        if (plan.sectors) allSectors.push(...plan.sectors)
        if (plan.boundaryActions) allActions.push(...plan.boundaryActions)
      }
      if (allSectors.length > 0) {
        sectorsAdapter.upsertMany(state.sectors, allSectors)
      }
      if (allActions.length > 0) {
        boundaryActionsAdapter.upsertMany(state.boundaryActions, allActions)
      }
    },
    removePlan: (state, action: { payload: number }) => {
      const planId = action.payload
      plansAdapter.removeOne(state.plans, planId)
      // Remove associated sectors and boundary actions
      const sectorIds = Object.values(state.sectors.entities)
        .filter((s) => s?.irrigationPlanId === planId)
        .map((s) => s!.id)
      const actionIds = Object.values(state.boundaryActions.entities)
        .filter((a) => a?.irrigationPlanId === planId)
        .map((a) => a!.id)
      sectorsAdapter.removeMany(state.sectors, sectorIds)
      boundaryActionsAdapter.removeMany(state.boundaryActions, actionIds)
    },

    // Sector actions
    upsertSector: (state, action: { payload: PlanSector }) => {
      sectorsAdapter.upsertOne(state.sectors, action.payload)
    },
    removeSector: (state, action: { payload: number }) => {
      sectorsAdapter.removeOne(state.sectors, action.payload)
    },

    // Boundary action actions
    upsertBoundaryAction: (state, action: { payload: BoundaryAction }) => {
      boundaryActionsAdapter.upsertOne(state.boundaryActions, action.payload)
    },
    removeBoundaryAction: (state, action: { payload: number }) => {
      boundaryActionsAdapter.removeOne(state.boundaryActions, action.payload)
    },

    // UI actions
    selectPlan: (state, action: { payload: number | null }) => {
      state.selectedPlanId = action.payload
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(signOutAsync.fulfilled, () => {
        return initialState
      })
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        if (payload.irrigationPlans) {
          // Reset state and load all plans
          const plans = payload.irrigationPlans as IrrigationPlan[]
          const allSectors: PlanSector[] = []
          const allActions: BoundaryAction[] = []

          for (const plan of plans) {
            if (plan.sectors) allSectors.push(...plan.sectors)
            if (plan.boundaryActions) allActions.push(...plan.boundaryActions)
          }

          plansAdapter.setAll(state.plans, plans)
          sectorsAdapter.setAll(state.sectors, allSectors)
          boundaryActionsAdapter.setAll(state.boundaryActions, allActions)
        }
      }),
})

export const {
  upsertPlan,
  upsertManyPlans,
  removePlan,
  upsertSector,
  removeSector,
  upsertBoundaryAction,
  removeBoundaryAction,
  selectPlan,
} = irrigationPlansSlice.actions

// =============================================================================
// SELECTORS
// =============================================================================

// Base selectors from adapters
const plansSelectors = plansAdapter.getSelectors<RootState>(
  (state) => state.irrigationPlans.plans,
)

const sectorsSelectors = sectorsAdapter.getSelectors<RootState>(
  (state) => state.irrigationPlans.sectors,
)

const boundaryActionsSelectors = boundaryActionsAdapter.getSelectors<RootState>(
  (state) => state.irrigationPlans.boundaryActions,
)

// Export plan selectors
export const {
  selectAll: selectAllPlans,
  selectById: selectPlanById,
  selectIds: selectPlanIds,
  selectEntities: selectPlanEntities,
  selectTotal: selectPlanTotal,
} = plansSelectors

// Export sector selectors
export const {
  selectAll: selectAllSectors,
  selectById: selectSectorById,
  selectIds: selectSectorIds,
  selectEntities: selectSectorEntities,
} = sectorsSelectors

// Export boundary action selectors
export const {
  selectAll: selectAllBoundaryActions,
  selectById: selectBoundaryActionById,
  selectIds: selectBoundaryActionIds,
  selectEntities: selectBoundaryActionEntities,
} = boundaryActionsSelectors

// UI selectors
export const selectSelectedPlanId = (state: RootState) =>
  state.irrigationPlans.selectedPlanId

// Derived selectors

/**
 * Get all irrigation plans for a specific center pivot.
 */
export const selectPlansForPivot = createSelector(
  [selectAllPlans, (_state: RootState, centerPivotId: number) => centerPivotId],
  (plans, centerPivotId) =>
    plans.filter((p) => p.centerPivotId === centerPivotId),
)

/**
 * Get the currently active plan for a center pivot (if any).
 */
export const selectActivePlanForPivot = createSelector(
  [selectAllPlans, (_state: RootState, centerPivotId: number) => centerPivotId],
  (plans, centerPivotId) =>
    plans.find((p) => p.centerPivotId === centerPivotId && p.state === "ACTIVE"),
)

/**
 * Get all sectors for a specific plan.
 */
export const selectSectorsForPlan = createSelector(
  [selectAllSectors, (_state: RootState, planId: number) => planId],
  (sectors, planId) =>
    sectors.filter((s) => s.irrigationPlanId === planId && s.isActive),
)

/**
 * Get all boundary actions for a specific plan.
 */
export const selectBoundaryActionsForPlan = createSelector(
  [selectAllBoundaryActions, (_state: RootState, planId: number) => planId],
  (actions, planId) =>
    actions.filter((a) => a.irrigationPlanId === planId && a.isActive),
)

/**
 * Check if any plan for a pivot is currently active.
 */
export const selectHasActivePlanForPivot = createSelector(
  [selectActivePlanForPivot],
  (plan) => plan !== undefined,
)

// =============================================================================
// REDUCER EXPORT
// =============================================================================

export const irrigationPlansReducer: Reducer<IrrigationPlansState> =
  irrigationPlansSlice.reducer
